import { useApolloClient } from '@apollo/client';
import { Button, Card, Col, Form, message, Row } from 'antd';
import { Formik } from 'formik';
import { omit } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type WhatsappLiveChatModuleSpecsFragment } from '../../../../api/fragments/WhatsappLiveChatModuleSpecs';
import { type WhatsappLiveChatSettingsSpecFragment } from '../../../../api/fragments/WhatsappLiveChatSettingsSpec';
import {
    UpdateWhatsappLiveChatSettingsDocument,
    type UpdateWhatsappLiveChatSettingsMutation,
    type UpdateWhatsappLiveChatSettingsMutationVariables,
} from '../../../../api/mutations/updateWhatsappLiveChatSettings';
import { type WhatsappLiveChatSettingsInput } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

export type FormValues = WhatsappLiveChatSettingsInput;

export type UpdateAdyenPaymentSettingInnerPageProps = {
    module: WhatsappLiveChatModuleSpecsFragment;
    settings: WhatsappLiveChatSettingsSpecFragment;
};

const validator = validators.compose(
    validators.requiredNonEmptyString('displayName'),
    validators.requiredString('link')
);

const colSpan = { lg: 8, md: 12, xs: 24 };

const UpdateWhatsappLiveChatSettingInnerPage = ({ module, settings }: UpdateAdyenPaymentSettingInnerPageProps) => {
    const moduleId = module.id;
    const settingsId = settings.id;

    const { t } = useTranslation(['whatsappLiveChatModuleDetails']);
    const apolloClient = useApolloClient();
    const navigate = useNavigate();

    const initialValues = useMemo((): FormValues => omit(['id', 'liveChatModuleId'], settings), [settings]);

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('whatsappLiveChatModuleDetails:settingsDrawer.messages.updateSubmitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateWhatsappLiveChatSettingsMutation, UpdateWhatsappLiveChatSettingsMutationVariables>({
                    mutation: UpdateWhatsappLiveChatSettingsDocument,
                    variables: { moduleId, id: settingsId, settings: values },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('whatsappLiveChatModuleDetails:settingsDrawer.messages.updateSuccess'),
            });

            navigate(`/admin/system/modules/${moduleId}`);
        },
        [t, apolloClient, moduleId, settingsId, navigate]
    );

    const validate = useValidator(validator);

    return (
        <ConsolePageWithHeader
            footer={[
                <Button key="submit" form="whatsappLiveChatSettingsForm" htmlType="submit" type="primary">
                    {t('whatsappLiveChatModuleDetails:settingsDrawer.updateButton')}
                </Button>,
            ]}
            onBack={() => navigate(`/admin/system/modules/${moduleId}`)}
            title={t('whatsappLiveChatModuleDetails:settingsDrawer.title.update')}
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
                {({ handleSubmit }) => (
                    <Form
                        id="whatsappLiveChatSettingsForm"
                        layout="vertical"
                        name="whatsappLiveChatSettingsForm"
                        onSubmitCapture={handleSubmit}
                    >
                        <Card title={t('whatsappLiveChatModuleDetails:dividers.mainDetails')}>
                            <Row gutter={14}>
                                <Col {...colSpan}>
                                    <InputField
                                        {...t('whatsappLiveChatModuleDetails:settingsDrawer.fields.displayName', {
                                            returnObjects: true,
                                        })}
                                        name="displayName"
                                        required
                                    />
                                </Col>
                                <Col {...colSpan}>
                                    <InputField
                                        {...t('whatsappLiveChatModuleDetails:settingsDrawer.fields.link', {
                                            returnObjects: true,
                                        })}
                                        name="link"
                                    />
                                </Col>
                            </Row>
                        </Card>
                    </Form>
                )}
            </Formik>
        </ConsolePageWithHeader>
    );
};

export default UpdateWhatsappLiveChatSettingInnerPage;
